import { request } from '@umijs/max';
import dayjs from 'dayjs';
import { getNebulithInferenceProApiUrl } from '@/services/util';
import type { InferenceModelListItem } from './inferenceModel';

export type PlanType =
  | 'fixedQuantity'
  | 'trial'
  | 'monthly'
  | 'codingPlan'
  | 'payAsYouGo';

export type PlanStatus = 'listed' | 'unlisted';

export interface PlanQuotaConfig {
  planQuotaConfigId?: string;
  quotaAmount?: string;
  quotaAmountMicro?: number;
  resetPeriodUnit?: string;
  resetPeriodValue?: number;
}

export interface PlanListItem {
  createAt?: string;
  creditAmount?: string;
  creditAmountMicro?: number;
  description?: string;
  modelIds?: string[];
  modelList?: InferenceModelListItem[];
  planId: string;
  planName?: string;
  planType?: PlanType | string;
  priceAmount?: number;
  priceAmountYuan?: string;
  quotaConfigs?: PlanQuotaConfig[];
  status?: PlanStatus | string;
  tags?: string[];
  trialValidityDays?: number;
}

/** 套餐实例 list-page 嵌套的套餐结构，关联缺失时为空对象 */
export type PlanEmbedded = Pick<
  PlanListItem,
  | 'createAt'
  | 'creditAmount'
  | 'creditAmountMicro'
  | 'description'
  | 'planId'
  | 'planName'
  | 'planType'
  | 'priceAmount'
  | 'priceAmountYuan'
  | 'status'
  | 'trialValidityDays'
  | 'modelList'
>;

export interface PlanListParams {
  planName?: string;
  planType?: PlanType | string;
  inferenceModelId?: string;
  status?: PlanStatus | string;
}

export interface PlanListResult {
  list: PlanListItem[];
}

export const PLAN_PAYMENT_METHOD_LABELS: Record<string, string> = {
  fixedQuantity: '预付费',
  monthly: '预付费',
  codingPlan: '预付费',
  trial: '免费赠送',
  payAsYouGo: '后付费',
};

export const PLAN_STATUS_LABELS: Record<string, string> = {
  listed: '上架',
  unlisted: '下架',
};

const RESET_PERIOD_UNIT_LABELS: Record<string, string> = {
  hour: '小时',
  day: '天',
  month: '月',
  year: '年',
};

const PLAN_VALIDITY_EMPTY = '-';

export const PLAN_LONG_TERM_VALID = '长期有效';

export const PLAN_UNLIMITED_CREDIT = '无限制';

export interface PlanCreditAmountSource {
  planType?: PlanType | string;
  creditAmount?: string;
}

export interface PlanValiditySource {
  planType?: PlanType | string;
  trialValidityDays?: number;
}

export interface PlanExpireAtSource extends PlanValiditySource {
  effectiveEndAt?: string;
  naturalEndAt?: string;
}

/** 套餐有效期呈现：定量包/后付费长期有效，月付/Coding Plan 1个月，试用包 N 天 */
export function formatPlanValidityPeriod(source: PlanValiditySource): string {
  const { planType, trialValidityDays } = source;
  if (planType === 'fixedQuantity' || planType === 'payAsYouGo') {
    return PLAN_LONG_TERM_VALID;
  }
  if (planType === 'monthly' || planType === 'codingPlan') {
    return '1个月';
  }
  if (planType === 'trial') {
    const days = Number(trialValidityDays);
    return Number.isFinite(days) && days > 0
      ? `${days} 天`
      : PLAN_VALIDITY_EMPTY;
  }
  return PLAN_VALIDITY_EMPTY;
}

/** 套餐实例有效期展示：无失效时间则长期有效，否则直接展示 effectiveEndAt */
export function formatPlanInstanceEffectiveEndDisplay(
  effectiveEndAt?: string,
): string {
  if (!effectiveEndAt?.trim()) {
    return PLAN_LONG_TERM_VALID;
  }
  return effectiveEndAt.trim();
}

export function showMonthlyFields(planType?: string): boolean {
  return planType === 'monthly' || planType === 'codingPlan';
}

export function showPriceField(planType?: string): boolean {
  return planType !== 'payAsYouGo' && planType !== 'trial';
}

export function showQuotaAmountField(planType?: string): boolean {
  return planType !== 'payAsYouGo' && planType !== 'codingPlan';
}

export function showCodingQuotaConfigs(planType?: string): boolean {
  return planType === 'codingPlan';
}

export function formatPlanPrice(record: PlanListItem): string {
  if (record.planType === 'payAsYouGo') {
    return '按实际用量折算积分';
  }
  const suffix = record.planType === 'monthly' ? '元/月' : '元';
  return `${record.priceAmountYuan}${suffix}`;
}

/** @deprecated 使用 formatPlanValidityPeriod */
export function formatPlanValidity(record: PlanListItem): string {
  return formatPlanValidityPeriod(record);
}

export function formatPlanCreditAmount(record: PlanCreditAmountSource): string {
  const planType = record.planType;
  if (planType === 'codingPlan' || planType === 'payAsYouGo') {
    return PLAN_UNLIMITED_CREDIT;
  }
  const amount = record.creditAmount;
  if (amount == null || String(amount).trim() === '') {
    return PLAN_VALIDITY_EMPTY;
  }
  return `${amount} 积分`;
}

export function formatSupportedModels(record: PlanListItem): string {
  const names = getPlanModelNames(record);
  return names.length ? names.join('、') : '-';
}

export function formatQuotaConfigLine(config: PlanQuotaConfig): string {
  const unit =
    RESET_PERIOD_UNIT_LABELS[config.resetPeriodUnit || ''] ||
    config.resetPeriodUnit ||
    '';
  const value = config.resetPeriodValue;
  if (!value || !unit || !config.quotaAmount) {
    return '-';
  }
  const num = Number(config.quotaAmount);
  const formatted = Number.isFinite(num)
    ? num.toLocaleString('zh-CN')
    : config.quotaAmount;
  return `每 ${value} ${unit}最多消耗 ${formatted} 积分`;
}

export function getPlanModelNames(record: PlanListItem): string[] {
  return (
    record.modelList?.map((item) => item.modelName?.trim()).filter(Boolean) as
      | string[]
      | undefined
  )?.length
    ? (record.modelList
        ?.map((item) => item.modelName?.trim())
        .filter(Boolean) as string[])
    : [];
}

export function getPlanUnitPriceYuan(record: PlanListItem): number {
  const yuan = Number.parseFloat(record.priceAmountYuan ?? '');
  if (Number.isFinite(yuan)) {
    return yuan;
  }
  if (record.priceAmount != null) {
    return record.priceAmount / 100;
  }
  return 0;
}

export function formatPlanDetailPrice(record: PlanListItem): string {
  if (record.planType === 'payAsYouGo') {
    return '按实际用量折算积分';
  }
  return `${record.priceAmountYuan} 元`;
}

export interface PlanPurchaseParams {
  planId: string;
}

export interface PlanModelBillingItem {
  businessQuantity?: number;
  creditAmount?: string;
  creditAmountMicro?: number;
  measureWord?: string;
  meterTypeCode?: string;
  meterTypeId?: string;
  meterTypeName?: string;
  modelId?: string;
  modelName?: string;
  modelTypeCode?: string;
  modelTypeId?: string;
  modelTypeName?: string;
  moneyAmount?: number | null;
  moneyAmountYuan?: string | null;
}

export interface PlanModelBillingListParams {
  planId: string;
}

export interface PlanModelBillingListResult {
  list: PlanModelBillingItem[];
}

export function formatPlanModelBillingMeteredQuantity(
  item: PlanModelBillingItem,
): string {
  const quantity = item.businessQuantity;
  const measureWord = item.measureWord?.trim();
  if (quantity == null || !measureWord) {
    return PLAN_VALIDITY_EMPTY;
  }
  const formattedMeasureWord =
    measureWord.charAt(0).toUpperCase() + measureWord.slice(1);
  return `${quantity.toLocaleString('zh-CN')} ${formattedMeasureWord}`;
}

export function formatPlanModelBillingCreditAmount(
  creditAmount?: string,
): string {
  if (creditAmount == null || !String(creditAmount).trim()) {
    return PLAN_VALIDITY_EMPTY;
  }
  return `${creditAmount} 积分`;
}

export function formatPlanModelBillingUnitPrice(
  moneyAmountYuan?: string | null,
): string | undefined {
  if (moneyAmountYuan == null || !String(moneyAmountYuan).trim()) {
    return undefined;
  }
  return `¥ ${moneyAmountYuan}`;
}

/** 套餐模型计费列表 */
export async function getPlanModelBillingList(
  params: PlanModelBillingListParams,
  options?: { skipErrorHandler?: boolean },
) {
  const res = await request<
    PlanModelBillingListResult | PlanModelBillingItem[]
  >(getNebulithInferenceProApiUrl('plan/model-billing/list'), {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
  if (Array.isArray(res)) {
    return res;
  }
  return res?.list ?? [];
}

/** 提交套餐购买 */
export async function purchasePlan(
  params: PlanPurchaseParams,
  options?: { skipErrorHandler?: boolean },
) {
  return request<unknown>(getNebulithInferenceProApiUrl('plan/purchase'), {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 购买页套餐列表（仅上架） */
export async function getPlanList(
  params?: PlanListParams,
  options?: { skipErrorHandler?: boolean },
) {
  const res = await request<PlanListResult | PlanListItem[]>(
    getNebulithInferenceProApiUrl('plan/list'),
    {
      method: 'POST',
      data: {
        ...params,
        status: params?.status ?? 'listed',
      },
      ...(options || {}),
    },
  );
  if (Array.isArray(res)) {
    return res;
  }
  return res?.list ?? [];
}
